import { useDebounceCallback } from '@shared/hooks/useDebounceCallback'
import { InputField } from '@shared/InputField'
import IMask from 'imask/holder'
import React, { useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { OrderSalaryCardType } from 'store/types/salaryCard'
import {
  useLazyFetchTrustedPersonByPinQuery,
  useLazySetByMeQuery,
} from 'store/reducers/salaryCards/trustedPerson.api'
import { Checkbox, Col, Form, Row, Space, Tooltip, Typography } from 'antd'
import DatePicker from '@shared/DatePicker/DatePicker'
import SelectTrustedPerson from './SelectTrustedPerson'
import { InfoCircleOutlined } from '@ant-design/icons'
import moment from 'moment'
import { ModalProvider } from '@shared/Modal'
import ButtonGlobal from '@shared/Button'
import Dialog from '@shared/Dialog'
import { useAbility } from '@shared/AccessManager'

function TrustedPersonForm() {
  const { t } = useTranslation()
  const ability = useAbility()

  const { control, trigger, setValue } = useFormContext<OrderSalaryCardType>()
  const [fetchTrustedPersonByPin] = useLazyFetchTrustedPersonByPinQuery()

  const watchTrustedPin = useWatch({ control, name: 'trustedPerson.pin' });

  const [setByMe, { isFetching, isUninitialized, isSuccess, isError, reset, data }] =
    useLazySetByMeQuery()

  const fetchTrustedPerson = async (pin: string) => {
    const isValid = await trigger('trustedPerson.pin')
    if (isValid) {
      fetchTrustedPersonByPin({ pin })
        .unwrap()
        .then((data) => {
          setValue('trustedPerson', data, { shouldValidate: true })
        })
        .catch(() => {
          setValue('trustedPerson.id', null, { shouldValidate: true })
          setValue('trustedPerson.fullName', '', { shouldValidate: true })
          setValue('trustedPerson.phone', '', { shouldValidate: true })
          setValue('trustedPerson.expiryDate', '', { shouldValidate: true })
        })
    }
  }

  const debounce = useDebounceCallback((value) => {
    setValue('trustedPerson.pin', value.maskedValue, {
      shouldValidate: true,
    })

    fetchTrustedPerson(value.maskedValue)
  }, 500)

  const setByMeFn = async (event) => {
    if (event.target.checked) {
      try {
        const data = await setByMe().unwrap()
        setValue('trustedPerson.id', data.id)
        setValue('trustedPerson.expiryDate', data.expiryDate, {
          shouldValidate: true,
        })
        setValue('trustedPerson.fullName', data.fullName, {
          shouldValidate: true,
        })
        setValue('trustedPerson.pin', data.pin, { shouldValidate: true })
        setValue('trustedPerson.phone', data.phone, {
          shouldValidate: true,
        })
      } catch (error) {
        Dialog.warning({
          title: t('common.attention'),
          modalType: 'failed',
          message: error?.data,
          okText: t('orders.salaryCardOrder.buttonsLabels.close'),
        })
      }
    } else {
      reset()
      setValue('trustedPerson.id', null, { shouldValidate: true })
      setValue('trustedPerson.pin', '', { shouldValidate: true })
      setValue('trustedPerson.fullName', '', { shouldValidate: true })
      setValue('trustedPerson.phone', '', { shouldValidate: true })
      setValue('trustedPerson.expiryDate', '', { shouldValidate: true })
    }
  }

  useEffect(() => {
    if(data?.pin && data?.pin !== watchTrustedPin) {
      reset()
    }
  }, [watchTrustedPin])
  

  return (
    <Row
      style={{
        boxSizing: 'border-box',
        backgroundColor: '#F3F6FA',
        borderRadius: 16,
        padding: 20,
        flexDirection: 'column',
        height: '100%'
      }}
      gutter={[0, 24]}
    >
      <Col>
        <Row gutter={[0, 24]} align="middle" justify="space-between">
          <Col>
            <Space>
              <Typography.Text style={{ fontSize: 18 }} strong>
                {t('orders.salaryCardOrder.constants.trustedPersonTitle')}
              </Typography.Text>
              <Tooltip
                title={t('orders.salaryCardOrder.constants.trustedPersonInfo')}
              >
                <InfoCircleOutlined
                  style={{ fontSize: 16, cursor: 'pointer', color: '#015CD1' }}
                />
              </Tooltip>
            </Space>
          </Col>
          <Col>
            <Checkbox
              checked={isUninitialized ? false : data?.pin === watchTrustedPin && !isError}
              disabled={ability.can('disabled', 'ItsMeButton') || isFetching}
              onChange={setByMeFn}
            >
              {t('orders.my')}
            </Checkbox>
          </Col>
          <Col span={12}>
            <Row align="middle" gutter={12} justify="space-between">
              <Col>
                <ModalProvider
                  bodyStyle={{
                    maxHeight: 'calc(100vh - 200px)',
                    overflowY: 'auto',
                  }}
                  title={t(
                    'orders.salaryCardOrder.constants.trustedPersonTitle'
                  )}
                  width={800}
                  modalContent={<SelectTrustedPerson />}
                >
                  {({ openModal }) => (
                    <ButtonGlobal
                      disabled={isFetching}
                      customType="primary"
                      onClick={() => openModal('')}
                    >
                      {t('orders.salaryCardOrder.buttonsLabels.makeSelection')}
                    </ButtonGlobal>
                  )}
                </ModalProvider>
              </Col>
              <Col>
                <Controller
                  name="trustedPerson.pin"
                  control={control}
                  render={({
                    field: { onChange, ...fields },
                    fieldState: { error, invalid },
                  }) => (
                    <InputField
                      disabled={isFetching || isSuccess}
                      label={t('orders.salaryCardOrder.inputLabels.pin')}
                      placeholder={t('orders.enters')}
                      style={{ margin: 0 }}
                      mask={IMask.MaskedPattern}
                      maskOptions={{
                        mask: /^[A-Za-z0-9]{0,20}$/,
                        prepare: (str) => str.toUpperCase(),
                      }}
                      validateStatus={invalid ? 'error' : null}
                      help={invalid ? error?.message : ''}
                      onChange={(value) => debounce(value)}
                      {...fields}
                    />
                  )}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>

      <Col>
        <Controller
          name="trustedPerson.fullName"
          control={control}
          render={({ field, fieldState: { error, invalid } }) => (
            <InputField
              label={t('orders.salaryCardOrder.inputLabels.fullName')}
              placeholder={t('orders.fullNamePlace')}
              labelCol={{ span: 4 }}
              disabled={isFetching || isSuccess}
              wrapperCol={{ span: 20 }}
              style={{ margin: 0 }}
              validateStatus={invalid ? 'error' : 'success'}
              help={invalid ? error?.message : ''}
              {...field}
            />
          )}
        />
      </Col>

      <Col>
        <Row gutter={12}>
          <Col span={12}>
            <Controller
              name="trustedPerson.phone"
              control={control}
              render={({ field, fieldState: { error, invalid } }) => {
                return (
                  <InputField
                    disabled={isFetching}
                    label={t('orders.salaryCardOrder.inputLabels.mobileNumber')}
                    labelCol={{ span: 8 }}
                    validateStatus={invalid ? 'error' : null}
                    style={{ margin: 0 }}
                    help={invalid ? error?.message : ''}
                    mask={IMask.MaskedPattern}
                    maskOptions={{
                      mask: '(##) 000-00-00',
                      blocks: {
                        '##': {
                          mask: IMask.MaskedEnum,
                          enum: [
                            '50',
                            '51',
                            '55',
                            '70',
                            '77',
                            '90',
                            '99',
                            '10',
                            '11',
                          ],
                        },
                      },
                      definitions: {
                        '0': /[0-9]/,
                      },
                    }}
                    {...field}
                  />
                )
              }}
            />
          </Col>
          <Col span={12}>
            <Controller
              name="trustedPerson.expiryDate"
              control={control}
              render={({
                field: { name, value, onChange, ...fields },
                fieldState: { error, invalid },
              }) => {
                return (
                  <Form.Item
                    labelCol={{ span: 8 }}
                    style={{ margin: 0 }}
                    disabled={isFetching}
                    label={t('orders.salaryCardOrder.inputLabels.expiryDate')}
                  >
                    <DatePicker
                      name={name}
                      onChange={(_date) =>
                        onChange(
                          moment(_date, 'DD.MM.YYYY').format('DD.MM.YYYY')
                        )
                      }
                      allowClear={false}
                      {...(value ? { value: moment(value, 'DD.MM.YYYY') } : {})}
                      format="DD.MM.YYYY"
                      suffixConfig={{
                        icon: <InfoCircleOutlined />,
                        text: t(
                          'orders.salaryCardOrder.restrictionTexts.invalidDateRange'
                        ),
                      }}
                      isError={invalid}
                      placeholder={t('orders.chooseDate')}
                      errorMessage={error?.message}
                    />
                  </Form.Item>
                )
              }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default TrustedPersonForm
